import axios from "axios";
import { getApiBaseUrl } from "../config/api";

export function normalizeEnrollmentStatus(value) {
    const v = String(value ?? "Verified").trim();
    if (/^pending$/i.test(v)) return "Pending";
    if (/^rejected$/i.test(v)) return "Rejected";
    return "Verified";
}

function asStudentArray(data) {
    if (Array.isArray(data)) return data;
    return [];
}

/** Map Students list row to enrollment review shape (with sclass + submitter). */
export async function fetchSchoolStudents(schoolId) {
    const { data } = await axios.get(`${getApiBaseUrl()}/Students/${schoolId}`);
    return asStudentArray(data);
}

export async function fetchPendingEnrollments(schoolId) {
    if (!schoolId) return { list: [], source: "none" };

    try {
        const { data } = await axios.get(
            `${getApiBaseUrl()}/Enrollment/school/${schoolId}?status=Pending`,
        );
        const list = asStudentArray(data);
        if (list.length > 0 || (Array.isArray(data) && data.length === 0)) {
            return {
                list: list.filter((s) => normalizeEnrollmentStatus(s.enrollmentStatus) === "Pending"),
                source: "enrollment",
            };
        }
    } catch {
        /* fall through to Students list */
    }

    try {
        const students = await fetchSchoolStudents(schoolId);
        const pending = students
            .filter((s) => normalizeEnrollmentStatus(s.enrollmentStatus) === "Pending")
            .map((s) => ({
                ...s,
                sclass: s.sclassName && typeof s.sclassName === "object" ? s.sclassName : s.sclass,
            }));
        return { list: pending, source: "students" };
    } catch {
        return { list: [], source: "error" };
    }
}

export async function fetchSchoolDeclarations(schoolId) {
    if (!schoolId) return [];
    try {
        const { data } = await axios.get(`${getApiBaseUrl()}/Enrollment/declarations/school/${schoolId}`);
        return asStudentArray(data);
    } catch {
        return [];
    }
}

async function requestWithFallback(methods, urls, body) {
    let lastError;
    for (const method of methods) {
        for (const url of urls) {
            try {
                const res = await axios({ method, url, data: body });
                return res;
            } catch (err) {
                lastError = err;
                const status = err.response?.status;
                if (status !== 404 && status !== 405) {
                    throw err;
                }
            }
        }
    }
    throw lastError;
}

/** Approve a pending enrollment (Enrollment API, then Student fallback). */
export async function approveEnrollment(studentId, approvedById) {
    const base = getApiBaseUrl();
    const body = { approvedById };
    const res = await requestWithFallback(
        ["put", "post"],
        [
            `${base}/Enrollment/student/${studentId}/approve`,
            `${base}/Student/${studentId}/enrollment/approve`,
        ],
        body,
    );
    return res.data;
}

/** Reject a pending enrollment (Enrollment API, then Student fallback). */
export async function rejectEnrollment(studentId, { approvedById, rejectionNote = "" }) {
    const base = getApiBaseUrl();
    const body = { approvedById, rejectionNote };
    const res = await requestWithFallback(
        ["put", "post"],
        [
            `${base}/Enrollment/student/${studentId}/reject`,
            `${base}/Student/${studentId}/enrollment/reject`,
        ],
        body,
    );
    return res.data;
}
